// Route guard quyền cho hub SaaS — lớp phòng thủ THÊM, không phải nguồn sự thật.
// Nguồn sự thật là requirePermission ở BE; guard này chỉ để user không gõ URL vào được trang
// trắng/403. Trước 2026-07-15 SaaS KHÔNG có guard nào: <RequireAuth> (App.jsx) chỉ check token
// nên ẩn menu là ảo giác — gõ thẳng URL là vào (audit P0-A).
//
// KHÁC Core (attendance-app/admin/src/navigation/adminMenu.js:506):
//   1. Core giữ PATH_TO_MODULE — một map TAY, tách rời menu → drift. SaaS derive thẳng từ
//      NAV_GROUPS (mỗi item đã có sẵn field `permission`) ⇒ menu và route KHÔNG THỂ lệch nhau.
//   2. Core FAIL-OPEN (`if (!matchedPath) return true`) → path lạ lọt hết. SaaS FAIL-CLOSED:
//      không khớp gì → CHẶN. Bù lại phải khai báo tường minh ALWAYS_ALLOWED + EXTRA_GATES dưới.
//
// ⚠️ Thêm <Route> mới vào App.jsx mà path không nằm dưới path menu nào thì PHẢI thêm vào
// ALWAYS_ALLOWED hoặc EXTRA_GATES, nếu không user thường sẽ bị đá về /dashboard.
// tools/check-route-guard-coverage.js gác việc này.

// Route cần đăng nhập nhưng KHÔNG gắn quyền — self-service, điều hướng, hoặc trang lỗi.
// Regex khớp TOÀN BỘ pathname.
export const ALWAYS_ALLOWED = [
  /^\/$/,                       // index → redirect /dashboard
  // Đường dẫn CŨ của trang gói, nay chỉ còn <Navigate> sang /billing (đổi tên 29.08.2026).
  // Không khai ở đây thì guard fail-closed CHẶN THẲNG người có bookmark cũ, thay vì chuyển
  // hướng họ — admin có '*' nên bấm thử không bao giờ lộ. Đích /billing vẫn gắn quyền đủ.
  /^\/marketplace$/,
  /^\/dashboard$/,              // trang chủ, mọi vai trò
  /^\/select-workspace$/,       // chọn workspace (chưa có active tenant)
  /^\/setting-up$/,             // màn hình chờ provisioning
  /^\/me$/,                     // hồ sơ cá nhân
  /^\/profile(\/.*)?$/,         // hồ sơ cá nhân + /profile/payroll = phiếu lương CỦA MÌNH
  /^\/notifications(\/.*)?$/,   // thông báo cá nhân
  /^\/chat(\/.*)?$/,            // chat nội bộ — gate bằng private_modules.team_chat (menu lo)
  /^\/reports(\/.*)?$/,         // nhóm Báo cáo cố ý KHÔNG gate (memory project_rbac_inbox_1to1)
  /^\/automation(\/.*)?$/,      // menu single-link không gắn permission
  /^\/suppliers(\/.*)?$/,       // menu Tiện ích không gắn permission
  // Module BỎ khỏi CNV Connect (2026-08-13) — route chỉ còn <Navigate to="/dashboard">.
  // Vẫn phải khai ở đây vì guard này chạy TRƯỚC Outlet: fail-closed cũng đá về /dashboard nên
  // kết quả y hệt, nhưng khai tường minh thì check-route-guard-coverage.js xanh và người sau
  // đọc là biết "cố ý cho khớp route rồi redirect", không phải "quên gate".
  // Bật lại module nào thì XOÁ dòng đó (menu sẽ tự derive gate như cũ).
  /^\/inbox(\/.*)?$/,
  /^\/crm(\/.*)?$/,
  /^\/marketing(\/.*)?$/,
  /^\/recruitment(\/.*)?$/,
  // Cụm TẮT 27/08/2026 (sếp Phú) — cùng diện: route chỉ còn <Navigate to="/dashboard">.
  // `/support/*` trước đây nằm ở nhóm trên với lý do "nhân viên tự tạo/xem ticket của mình";
  // nay cả họ /tickets/* lẫn /support/* đều là chuyển hướng nên xuống đây cho đúng chỗ.
  /^\/tickets(\/.*)?$/,
  /^\/support(\/.*)?$/,
  /^\/penalties$/,
  /^\/employees\/events$/,
];

// Path KHÔNG có menu item (nên không derive được) nhưng VẪN phải gate.
// payroll: menu "Lương" đang comment ở AppLayout (tạm ẩn) → không derive được, mà trang thì vẫn
// route được. Bỏ trống = ai gõ /payroll/periods cũng vào xem lương cả công ty.
// KHÔNG gồm /profile/payroll (phiếu lương của chính mình — đã ở ALWAYS_ALLOWED).
export const EXTRA_GATES = {
  '/payroll': 'payroll:view',
  // «Ca làm việc» (mẫu ca HR) + «Lịch làm việc» (phân ca HR) GỠ KHỎI MENU 30/09/2026 — §7.1 ca = việc, xem ghi chú
  // ở AppLayout.jsx. Trang vẫn mở theo địa chỉ, gác đúng quyền cũ; thiếu hai dòng này thì guard fail-closed đá người
  // thường về /dashboard. (`/shifts/credit-rules` vẫn là mục menu — dài hơn nên thắng dòng `/shifts`.)
  '/shifts': 'shifts:view',
  '/attendance/roster': 'attendance_list:view',
  '/finance/domains': 'domains:view',
  // `/jobsite` GỠ 21/08/2026 cùng bảy trang `/jobsite/*`: ảnh nay sống TRONG VIỆC, `App.jsx`
  // không mount một route `jobsite` nào nữa (đếm được: 0). Giữ lại một dòng gác cho đường
  // không tồn tại là nợ đọng — lần sau ai đọc bảng này lại tưởng cụm trang đó còn.
  // Alias /recruitment/pipelines GỠ cùng module Tuyển dụng (2026-08-13): App.jsx không mount
  // họ path đó nữa, /recruitment/* redirect thẳng về /dashboard. Thư viện Pipeline giờ chỉ gate
  // qua menu item `system-pipelines` (/settings/pipelines, nhóm Tiện ích) — derive tự động từ
  // NAV_GROUPS nên không cần khai ở đây.
};

/**
 * Derive [{ path, permission }] từ NAV_GROUPS, sắp path DÀI TRƯỚC để longest-prefix thắng
 * (vd /crm/leads phải thắng /crm).
 */
export function buildRouteMap(navGroups) {
  const out = [];
  const push = (path, permission) => { if (path && permission) out.push({ path, permission }); };
  for (const g of navGroups || []) {
    push(g.path, g.permission);
    for (const it of g.items || []) push(it.path, it.permission);
  }
  for (const [path, permission] of Object.entries(EXTRA_GATES)) push(path, permission);
  return out.sort((a, b) => b.path.length - a.path.length);
}

/**
 * FAIL-CLOSED.
 * @param {string}   pathname
 * @param {Array}    routeMap  — từ buildRouteMap(), đã sắp dài-trước
 * @param {Function} hasPerm   — (permission) => boolean; dùng CHUNG hàm đang lọc menu để guard và
 *                               menu không thể lệch logic (admin bypass, '*' bypass)
 * @param {boolean}  isAdmin   — bypass toàn bộ (khớp BE: requirePermission cho admin qua thẳng)
 */
export function checkRoutePermission(pathname, routeMap, hasPerm, isAdmin) {
  if (!pathname) return false;
  if (isAdmin) return true;
  if (ALWAYS_ALLOWED.some((rx) => rx.test(pathname))) return true;

  const hit = routeMap.find((r) => pathname === r.path || pathname.startsWith(`${r.path}/`));
  if (!hit) return false;            // ⛔ fail-closed — KHÁC Core (Core: return true)
  return hasPerm(hit.permission);
}
